/**
 * PointageTerrain — Interface de pointage simplifiée pour les employés terrain
 * (chef_equipe, peintre)
 * Affiche uniquement les bons de travail du jour et permet de pointer
 * sans avoir à chercher son nom dans une liste.
 */
import axios from 'axios';
import { useState, useEffect, useRef, useCallback } from "react";
import { dateDuJourQuebec } from '@/lib/dateQuebec';  // 🧱 Phase 4 — 4.1 (B-1)
import { bonsDuJourSeulement } from '@/lib/btPointageDuJour';  // 🧱 Phase 4 — B-26
import { formatDureeLive } from '@/lib/dureeFormat';  // 🎨 [v13.54] format non-ambigu
import { reportClientEvent } from '@/lib/clientDiagnostics';  // 🔎 [BLOC 2 v13.62] instrumentation Pause PM
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Play, Square, Coffee, RefreshCw, MapPin, Clock, CheckCircle,
  Building2, Calendar, WifiOff, Wifi, CloudUpload, Home, Trees, Layers,
  Droplets, ArrowRight, Wrench, LogOut as LogOutIcon, LifeBuoy, Briefcase
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "@/lib/PermissionsContext";
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// Cat H — dispatch UI selon type d'employé : TERRAIN_ROLES incarnent l'invariant "employé terrain" (peintre/chef/sous_traitant) qui a une UI de pointage différente des admins.
const TERRAIN_ROLES = ['chef_equipe', 'peintre', 'sous_traitant'];

// ─── Offline helpers ──────────────────────────────────────────────
const OQ_KEY = 'prqc_terrain_queue';
const CP_KEY = 'prqc_terrain_current_punch';
const getQueue = () => { try { return JSON.parse(localStorage.getItem(OQ_KEY) || '[]'); } catch { return []; } };
const saveQueue = (q) => localStorage.setItem(OQ_KEY, JSON.stringify(q));
const getCurrentPunch = () => { try { return JSON.parse(localStorage.getItem(CP_KEY) || 'null'); } catch { return null; } };
const saveCurrentPunch = (p) => localStorage.setItem(CP_KEY, JSON.stringify(p));
const clearCurrentPunch = () => localStorage.removeItem(CP_KEY);

// ─── 🆕 [Phase v13.60.5 — 2026-08-14] Cache local pause PM ─────────
// Clé localStorage scopée par jour local QC + employé, expirée
// naturellement au changement de date. Sert de mémoire "j'ai déjà
// répondu OUI/NON aujourd'hui" pour éviter tout faux positif si le
// re-fetch réseau `/pause-pm-applicable` échoue au dépunch.
const _pausePmLocalKey = (empId) => {
  // Date locale QC (America/Toronto)
  const d = new Date();
  const iso = d.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
  return `prqc_pause_pm_declared_${iso}_${empId || 'anon'}`;
};
const pausePmLocalCache = {
  markDeclared: (empId) => {
    try { localStorage.setItem(_pausePmLocalKey(empId), '1'); } catch { /* ignore */ }
  },
  hasDeclared: (empId) => {
    try { return localStorage.getItem(_pausePmLocalKey(empId)) === '1'; } catch { return false; }
  },
  // Nettoie les clés d'autres jours (housekeeping léger).
  cleanupStale: () => {
    try {
      const todayKey = _pausePmLocalKey('__scan__').replace('__scan__', '');
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith('prqc_pause_pm_declared_') && !k.startsWith(todayKey)) {
          localStorage.removeItem(k);
        }
      }
    } catch { /* ignore */ }
  },
};

// ─── 🆕 [Phase v13.60.5] Fetch avec retry + backoff exponentiel ────
// 2 tentatives max, backoff 500ms puis 1500ms. Retourne `null` si
// toutes les tentatives échouent — l'appelant décide alors du
// comportement (toast bloquant ou fallback via cache local).
const fetchPausePmApplicableWithRetry = async (headers) => {
  const attempts = [0, 500, 1500];
  let lastErr = null;
  let lastStatus = null;
  for (const wait of attempts) {
    if (wait > 0) {
      await new Promise((r) => setTimeout(r, wait));
    }
    try {
      const res = await axios.get(
        `${API_URL}/api/punch/pause-pm-applicable`,
        { headers, timeout: 5000 }
      );
      if (res?.status === 200 && res.data) {
        // 🔎 [BLOC 2 v13.62] Instrumentation — trace TOUT résultat de check.
        //   `applicable` peut être false pour 6 raisons distinctes (voir punch.py
        //   L1978-2102). Ce log permet de savoir laquelle bloque la modale en prod
        //   sans avoir à écrire les logs backend.
        try {
          reportClientEvent('pause_pm_check_result', 'info', {
            applicable: !!res.data.applicable,
            reason: res.data.reason || null,
            total_brut_jour_h: res.data.total_brut_jour_h ?? null,
            total_net_jour_h: res.data.total_net_jour_h ?? null,
            seuil_hours: res.data.seuil_hours ?? null,
            date_locale_qc: res.data.date_locale_qc ?? null,
            date_demarrage: res.data.date_demarrage ?? null,
          });
        } catch { /* jamais throw */ }
        return res.data;
      }
    } catch (e) {
      lastErr = e;
      lastStatus = e?.response?.status || null;
    }
  }
  console.warn('[PAUSE-PM v13.60.5] Re-fetch échoué après 3 tentatives:', lastErr?.message || lastErr);
  // 🔎 [BLOC 2 v13.62] Instrumentation — trace les échecs de retry.
  try {
    reportClientEvent('pause_pm_retry_failed', 'warning', {
      attempts_count: attempts.length,
      last_status: lastStatus,
      last_error_message: (lastErr?.message || String(lastErr)).slice(0, 200),
    });
  } catch { /* jamais throw */ }
  return null;
};

// ─── Timer hook ───────────────────────────────────────────────────
function useElapsedTime(startTime) {
  // 🎨 [Phase v13.54 hotfix TC2 — 2026-07-22] Retour: format non-ambigu "Xh YYmin".
  // Avant : "HH:MM:SS" (padStart '00:00:00'). Le tester TC2 a demandé
  // l'uniformisation totale avec MobileBottomNav / ChantierDuJour qui utilisent
  // déjà `formatDureeLive`. Comme ce hook n'est consommé qu'à l'affichage
  // (aucun parsing, split, comparaison — vérifié par grep), le changement de
  // format est safe. Tick 1s conservé pour ne pas altérer le comportement runtime
  // (optimisation possible en Phase 56 : passer à 30-60s puisque l'affichage
  // ne change qu'à la minute).
  const [elapsed, setElapsed] = useState(formatDureeLive(0));
  useEffect(() => {
    if (!startTime) { setElapsed(formatDureeLive(0)); return; }
    const update = () => {
      const diff = Math.floor((Date.now() - new Date(startTime).getTime()) / 1000);
      setElapsed(formatDureeLive(diff));
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [startTime]);
  return elapsed;
}

// ─── Icon par type de travaux ─────────────────────────────────────
function TypeIcon({ type }) {
  if (type === 'exterieur') return <Trees className="w-5 h-5 text-green-600" />;
  if (type === 'mixte') return <Layers className="w-5 h-5 text-purple-600" />;
  return <Home className="w-5 h-5 text-blue-600" />;
}

// ─── Carte d'une route de lavage en cours ──────────────────────────
// Affiche le chrono défilant pour rassurer l'employé que son punch fonctionne
// même si les actions "Terminer client" se font depuis la page Routes de Lavage.
function RouteLavageCard({ currentPunch }) {
  const navigate = useNavigate();
  const elapsed = useElapsedTime(currentPunch?.punch_in_time);
  const routeId = (currentPunch?.contrat_id || '').replace(/^lavage-/, '');
  const punchInTime = currentPunch?.punch_in_time
    ? new Date(currentPunch.punch_in_time).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <Card
      className="border-2 border-cyan-500 bg-gradient-to-br from-cyan-50 to-sky-50 shadow-lg"
      data-testid="route-lavage-active-card"
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Droplets className="w-5 h-5 text-cyan-600" />
            <div>
              <p className="font-semibold text-cyan-900 text-base leading-tight">
                Route de lavage à pression
              </p>
              <p className="text-xs text-cyan-700">
                Départ à {punchInTime}
              </p>
            </div>
          </div>
          <Badge className="bg-cyan-500 text-white text-xs px-2 py-1 animate-pulse">
            En cours
          </Badge>
        </div>

        {/* Timer actif — le vrai "chrono défilant" demandé */}
        <div className="bg-cyan-100 rounded-xl p-3 mb-3 text-center">
          <p className="text-3xl font-mono font-bold text-cyan-700 tracking-widest" data-testid="active-timer-lavage">
            {elapsed}
          </p>
          <p className="text-xs text-cyan-600 mt-1">Temps écoulé depuis le départ</p>
        </div>

        {/* CTA vers la page des routes pour terminer les clients */}
        <Button
          className="w-full bg-cyan-600 hover:bg-cyan-700 text-white h-11 text-base font-semibold"
          onClick={() => navigate('/routes-lavage', routeId ? { state: { selectedRouteId: routeId } } : undefined)}
          data-testid="open-routes-lavage-btn"
        >
          Ouvrir la route de lavage
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}

// ─── Carte d'un bon de travail ─────────────────────────────────────
function BonCard({ bon, currentPunch, employeeId, employeeNom, location, onPunchIn, onPunchOut, onPunchSwitch, loading }) {
  const isPunchedInHere = currentPunch?.contrat_id === bon.id;
  const isPunchedInElsewhere = currentPunch && !isPunchedInHere;
  const elapsed = useElapsedTime(isPunchedInHere ? currentPunch?.punch_in_time : null);

  // 🔧 Bug #5 — info ordre dans la journée
  const ordreIdx = bon.ordre_journee_index || 0;
  const totalJobs = bon.total_jobs_today || 0;
  const showJobLabel = totalJobs > 1 && ordreIdx > 0;
  const heureLabel = bon.heure_debut || '';

  return (
    <Card
      className={`transition-all duration-300 ${isPunchedInHere
        ? 'border-2 border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 shadow-lg'
        : 'border border-gray-200 bg-white'
      }`}
      data-testid={`bon-card-${bon.id}`}
    >
      <CardContent className="p-4">
        {/* 🔧 Bug #5 — Job N / Total — visible quand 2+ BT le même jour */}
        {showJobLabel && (
          <div className="flex items-center justify-between mb-2 pb-2 border-b border-dashed border-gray-200">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-700 bg-blue-50 rounded-full px-2.5 py-1">
              📍 Job {ordreIdx} / {totalJobs}
              {heureLabel && <span className="text-blue-500">• {heureLabel}</span>}
            </span>
            {!isPunchedInHere && isPunchedInElsewhere && (
              <span className="text-[10px] text-amber-600 font-semibold uppercase tracking-wide">
                ⏳ À faire après Job en cours
              </span>
            )}
          </div>
        )}

        {/* En-tête du bon */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <TypeIcon type={bon.type_travaux} />
            <div>
              <p className="font-semibold text-gray-900 text-base leading-tight">
                {bon.est_interne ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-orange-600">🔧</span>
                    {bon.nom_dossier || 'BT Interne'}
                  </span>
                ) : (bon.client_nom || bon.nom_dossier)}
              </p>
              {bon.est_interne && bon.categorie_interne_nom && (
                <p className="text-xs text-orange-700 font-medium">{bon.categorie_interne_nom}</p>
              )}
              {!bon.est_interne && bon.client_nom && bon.nom_dossier && (
                <p className="text-xs text-gray-500">{bon.nom_dossier}</p>
              )}
            </div>
          </div>
          {isPunchedInHere && (
            <Badge className="bg-green-500 text-white text-xs px-2 py-1 animate-pulse">
              En cours
            </Badge>
          )}
          {bon.is_chef && !isPunchedInHere && (
            <Badge variant="outline" className="text-blue-600 border-blue-200 text-xs">
              Chef d'équipe
            </Badge>
          )}
        </div>

        {/* Adresse */}
        {bon.adresse && (
          <div className="flex items-center gap-1.5 text-sm text-gray-600 mb-3">
            <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <span>{bon.adresse}</span>
          </div>
        )}

        {/* Heure de début prévue */}
        {bon.heure_debut && (
          <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-3">
            <Clock className="w-3.5 h-3.5 text-gray-400" />
            <span>Début prévu : {bon.heure_debut}</span>
          </div>
        )}

        {/* Timer actif */}
        {isPunchedInHere && (
          <div className="bg-green-100 rounded-xl p-3 mb-3 text-center">
            <p className="text-3xl font-mono font-bold text-green-700 tracking-widest" data-testid="active-timer">
              {elapsed}
            </p>
            <p className="text-xs text-green-600 mt-1">Temps écoulé depuis le punch</p>
            <p className="text-[10px] text-slate-400 mt-0.5">30 min de pause déduites automatiquement</p>
          </div>
        )}

        {/* Boutons */}
        <div className="flex gap-2 mt-2">
          {!currentPunch && (
            <Button
              className="flex-1 bg-green-600 hover:bg-green-700 text-white h-12 text-base font-semibold"
              onClick={() => onPunchIn(bon)}
              disabled={loading}
              data-testid={`punch-in-${bon.id}`}
            >
              <Play className="w-5 h-5 mr-2" />
              Commencer
            </Button>
          )}

          {isPunchedInHere && (
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white h-12 text-base font-semibold"
              onClick={() => onPunchOut(bon, 'fin')}
              disabled={loading}
              data-testid={`punch-out-${bon.id}`}
            >
              <Square className="w-5 h-5 mr-2" />
              Terminer
            </Button>
          )}

          {isPunchedInElsewhere && (
            <Button
              variant="outline"
              className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50 h-12"
              onClick={() => onPunchSwitch(bon)}
              disabled={loading}
              data-testid={`switch-${bon.id}`}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Basculer sur ce chantier
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Composant principal ──────────────────────────────────────────
export function PointageTerrain({ user }) {
  // Cat H — `isAdmin` = dispatch UI (admin voit sa punch admin sans BT), `isProprietaire` = variante UI supplémentaire (choix durée pause).
  const isAdmin = !TERRAIN_ROLES.includes(user?.role);
  const isProprietaire = estProprietaire(user);
  // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Branchement RBAC via `mon_espace_bts_renfort`.
  const { canDo, canDoBonTravailAction } = usePermissions() || {};
  const canVoirBtsRenfort = !!(canDo && canDo('mon_espace_bts_renfort'));
  // 🧱 Phase 4 — Lot C (D2) : nacelle/échafauds (commencer/prolonger/terminer-equipement) = chefs et plus,
  //   via la clé RBAC `bon_equipements_location` (jamais par rôle en dur). Sans la clé : aucun pop-up équipement.
  const canEquipements = !!(canDoBonTravailAction && canDoBonTravailAction('bon_equipements_location'));
  const [bons, setBons] = useState([]);
  const [currentPunch, setCurrentPunch] = useState(null);
  const [todayPunches, setTodayPunches] = useState([]);
  // 🔄 Phase A2.3 — Service Calls assignés au peintre pour aujourd'hui
  const [serviceCalls, setServiceCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [punching, setPunching] = useState(false);
  const [location, setLocation] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingQueue, setPendingQueue] = useState(getQueue);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseDuration, setPauseDuration] = useState('0.5');
  const [pendingPunchOut, setPendingPunchOut] = useState(null);
  const [adminElapsed, setAdminElapsed] = useState(() => formatDureeLive(0));
  const [pauseElapsed, setPauseElapsed] = useState('');
  const [pauseLoading, setPauseLoading] = useState(false);
  // 🔧 Bug #5 — Modal de bascule entre 2 BT
  const [switchTarget, setSwitchTarget] = useState(null); // { bon }
  // Pop-up nacelle
  const [nacellePopup, setNacellePopup] = useState(null); // { bon, type: 'debut' | 'fin', step: 1 | 2 }
  const syncRef = useRef(false);

  // 🆕 2026-05-07 — BTs en renfort pour CP/adjoint (ne touche pas à la logique principale)
  // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Whitelist rôle remplacée par `canVoirBtsRenfort` (canDo).
  //   Endpoint `/api/employees/mes-bts-renfort` reste auth-only (JWT identifie déjà l'employé).
  const [btsRenfort, setBtsRenfort] = useState([]);
  useEffect(() => {
    if (!isAdmin) return;
    if (!canVoirBtsRenfort) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/employees/mes-bts-renfort`, { headers: authHeaders });
        if (!cancelled && res.status === 200) {
          setBtsRenfort(res.data?.bons_aujourd_hui || []);
        }
      } catch { /* silencieux */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, canVoirBtsRenfort, currentPunch]);

  const token = localStorage.getItem('auth_token');
  const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  // ─── GPS périodique toutes les 60s pendant punch actif (terrain seulement) ──
  useEffect(() => {
    if (!currentPunch || currentPunch.statut !== 'en_cours') return;
    if (currentPunch.type_punch === 'admin') return;  // pas de GPS live en admin
    if (currentPunch.is_offline) return;

    let cancelled = false;
    const sendPosition = async () => {
      if (!navigator.geolocation) return;
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 30000,
          });
        });
        if (cancelled) return;
        await axios.post(`${API_URL}/api/punch/update-location`, {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }, { headers: authHeaders }).catch(() => {});
      } catch {
        /* silencieux : on réessaiera dans 60s */
      }
    };
    sendPosition();  // immédiatement
    const id = setInterval(sendPosition, 60000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPunch?.id, currentPunch?.statut, currentPunch?.type_punch]);

  // ─── Nacelle + Démontage pop-ups ───────────────────────────────────────────
  const [inspectionModal, setInspectionModal] = useState(null); // { unite, outils, inspection: {} }
  const [pendingPunchBon, setPendingPunchBon] = useState(null); // BT en attente après inspection
  // 🆕 2026-05-23 — Déclaration pause PM (15 min après-midi) pour employés terrain
  // à partir du 2026-05-25.
  const [pausePmApplicable, setPausePmApplicable] = useState(false);
  const [pausePmModal, setPausePmModal] = useState(null); // null | { bon, step: 'question'|'confirm-oui'|'confirm-non', pauseMin }

  // 🆕 [2026-06-11] Dialog "hors zone" au punch-IN.
  // Failsafe : si le check distance échoue (timeout/erreur/coords manquantes),
  // on n'ouvre PAS la modal et on laisse le punch passer normalement.
  // null | { bon, gps, distance_m, isServiceCall: bool, sc?: object }
  const [horsZoneConfirmIn, setHorsZoneConfirmIn] = useState(null);

  // Vérifier si l'inspection vendredi est requise
  const checkInspectionVendredi = useCallback(async (bon) => {
    const today = new Date();
    const isVendredi = today.getDay() === 5;
    if (!isVendredi) return false;
    if (!user?.employee_id) return false;
    // Trouver l'unité de rangement du chef
    try {
      const empRes = await axios.get(`${API_URL}/api/employees/${user.employee_id}`);
      const emp = empRes.data;
      const uniteId = emp?.unite_rangement_id || emp?.unites_assignees?.[0]?.unite_id;
      if (!uniteId) return false;
      // Vérifier si inspection déjà faite cette semaine
      const checkRes = await axios.get(`${API_URL}/api/unites-rangement/${uniteId}/inspection-requise`);
      if (checkRes.data?.requise) {
        // Charger les outils pour le formulaire d'inspection
        const outilsRes = await axios.get(`${API_URL}/api/unites-rangement/${uniteId}/outils`);
        const outils = outilsRes.data?.outils || [];
        if (outils.length > 0) {
          const init = {};
          outils.forEach(o => { init[o.id] = 'present'; });
          setInspectionModal({ unite: { id: uniteId }, outils, inspection: init });
          setPendingPunchBon(bon);
          return true; // Inspection requise — bloquer le punch
        }
      }
    } catch (e) { console.error("Error:", e); }
    return false;
  }, [user, API_URL]);
  const checkNacellePopup = (bon, type) => {
    if (!canEquipements) return;  // 🧱 Lot C (D2)
    if (!bon.nacelle_lavage_requise) return;
    const today = dateDuJourQuebec();
    const dates = bon.dates_chantier || [];
    if (type === 'debut') {
      const premierJour = dates.length > 0 ? [...dates].sort()[0] : null;
      if (premierJour !== today) return;
      if (bon.nacelle_lavage_utilisation_en_cours || bon.nacelle_lavage_debut_utilisation) return;
      // Nacelle laissée chez le client = pas de pop-up début
      if (bon.nacelle_retour === 'client') return;
      setNacellePopup({ bon, type: 'debut', step: 1 });
    } else if (type === 'fin') {
      const dernierJour = dates.length > 0 ? [...dates].sort().reverse()[0] : null;
      if (dernierJour !== today) return;
      if (bon.nacelle_retour === 'client') return; // Laissée chez client = pas de pop-up fin
      if (bon.nacelle_lavage_fin_utilisation) return;
      setNacellePopup({ bon, type: 'fin', step: 1 });
    }
  };

  const checkDemontagePopup = (bon, timing) => {
    if (!canEquipements) return;  // 🧱 Lot C (D2)
    const today = dateDuJourQuebec();
    const equipements = bon.equipements_selectionnes || bon.equipements || [];
    // Vérifier chaque équipement qui a des dates de montage/démontage
    for (const eq of equipements) {
      // Pop-up montage (veille)
      if (eq.date_montage && timing === 'veille_depunch') {
        const veille = new Date(eq.date_montage + 'T00:00:00');
        veille.setDate(veille.getDate() - 1);
        if (veille.toISOString().split('T')[0] === today) {
          setNacellePopup({ bon, type: 'montage_veille', step: 1, eq });
          return;
        }
      }
      // Pop-up démontage
      if (eq.date_demontage) {
        const veille = new Date(eq.date_demontage + 'T00:00:00');
        veille.setDate(veille.getDate() - 1);
        const veilleStr = veille.toISOString().split('T')[0];
        if (timing === 'veille_depunch' && today === veilleStr) {
          setNacellePopup({ bon, type: 'demontage_veille', step: 1, eq });
          return;
        }
        if (timing === 'matin_punch' && today === eq.date_demontage) {
          setNacellePopup({ bon, type: 'demontage_matin', step: 1, eq });
          return;
        }
        if (timing === 'soir_depunch' && today === eq.date_demontage) {
          setNacellePopup({ bon, type: 'demontage_soir', step: 1, eq });
          return;
        }
      }
    }
    // Fallback: vérifier l'ancien champ echafauds_date_demontage (rétrocompat)
    if (!bon.echafauds_a_monter || bon.echafauds_remorque_retour !== 'bureau') return;
    const dateDemontage = bon.echafauds_date_demontage;
    if (!dateDemontage) return;
    const veille = new Date(dateDemontage + 'T00:00:00');
    veille.setDate(veille.getDate() - 1);
    const veilleStr = veille.toISOString().split('T')[0];
    if (timing === 'veille_depunch' && today === veilleStr) {
      setNacellePopup({ bon, type: 'demontage_veille', step: 1 });
    } else if (timing === 'matin_punch' && today === dateDemontage) {
      setNacellePopup({ bon, type: 'demontage_matin', step: 1 });
    } else if (timing === 'soir_depunch' && today === dateDemontage) {
      setNacellePopup({ bon, type: 'demontage_soir', step: 1 });
    }
  };

  const confirmNacellePopup = async (reponse) => {
    if (!nacellePopup) return;
    const { bon, type, step } = nacellePopup;
    if (step === 1) {
      setNacellePopup({ ...nacellePopup, step: 2 });
      return;
    }
    // Step 2 — confirmation finale
    try {
      if (type === 'debut' && reponse === 'oui') {
        await axios.post(`${API_URL}/api/crm/bons-travail/${bon.id}/commencer-equipement`, {
          type_equipement: 'nacelle', employe_nom: `${user.prenom || ''} ${user.nom || ''}`.trim()
        });
        toast.success('✅ Utilisation de la nacelle confirmée');
      } else if (type === 'fin') {
        if (reponse === 'oui') {
          await axios.post(`${API_URL}/api/crm/bons-travail/${bon.id}/terminer-equipement`, { type_equipement: 'nacelle' });
          toast.success('✅ Nacelle marquée retournée — merci !');
        } else if (reponse === 'plus') {
          await axios.post(`${API_URL}/api/crm/bons-travail/${bon.id}/prolonger-equipement`, { type_equipement: 'nacelle', jours: 1 });
          toast.info('📅 Nacelle prolongée d\'1 jour');
        } else if (reponse === 'laisse') {
          await axios.post(`${API_URL}/api/crm/bons-travail/${bon.id}/terminer-equipement`, { type_equipement: 'nacelle', laisse_chez_client: true });
          toast.info('📍 Nacelle laissée chez le client — enregistré');
        }
      }
    } catch { toast.error('Erreur mise à jour nacelle'); }
    setNacellePopup(null);
    loadData();
  };

  // ─── Connexion / déconnexion ────────────────────────────────────
  useEffect(() => {
    const onOnline = () => { setIsOnline(true); toast.success('Connexion rétablie !'); syncOffline(); };
    const onOffline = () => { setIsOnline(false); toast.warning('Mode hors-ligne activé'); };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); };
  }, []);

  // 🆕 2026-05-23 — Au mount, vérifier si la déclaration pause PM s'applique à cet employé
  //
  // 🆕 [Phase v13.60.5 — 2026-08-14] Utilise le helper retry pour absorber les
  // hoquets réseau. Nettoie aussi le cache local de déclaration des jours
  // précédents pour éviter l'accumulation dans localStorage.
  useEffect(() => {
    let cancelled = false;
    pausePmLocalCache.cleanupStale();
    (async () => {
      const data = await fetchPausePmApplicableWithRetry(authHeaders);
      if (!cancelled && data) {
        setPausePmApplicable(!!data.applicable);
      }
      // Si data est null (échec) : on ne fait rien — le re-fetch avant punch-out
      // (handlePunchOut) est le point de vérité qui protège contre les zombies.
    })();
    return () => { cancelled = true; };
  }, []);

  // ─── Chargement initial ─────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (!isOnline) {
        const offlinePunch = getCurrentPunch();
        if (offlinePunch) setCurrentPunch(offlinePunch);
        return;
      }
      const res = await axios.get(`${API_URL}/api/punch/mes-bons-aujourd-hui`, { headers: authHeaders });
      if (res.status !== 200) throw new Error('Erreur chargement');
      const data = res.data;
      // 🧱 Refactor BT Phase 4 — B-26 (décision utilisatrice) : le Pointage n'affiche et ne permet de puncher
      //   que les bons d'AUJOURD'HUI ; ceux de demain restent visibles dans « Mon espace » (section DEMAIN).
      setBons(bonsDuJourSeulement(data.bons));
      setCurrentPunch(data.current_punch || null);
      setTodayPunches(data.today_punches || []);
      // 🔄 Phase A2.3 — Charger les SC du jour pour cet employé
      setServiceCalls(data.service_calls || []);
    } catch (e) {
      console.error(e);
      toast.error('Erreur de chargement des données');
      const offlinePunch = getCurrentPunch();
      if (offlinePunch) setCurrentPunch(offlinePunch);
    } finally {
      setLoading(false);
    }
  }, [isOnline]);

  useEffect(() => { loadData(); }, []);

  // ─── Géolocalisation ────────────────────────────────────────────
  // GPS est maintenant demandé obligatoirement à chaque punch (voir handlePunchIn)
  // On essaie de pré-charger la position pour l'indicateur visuel
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => {},
        { timeout: 5000 }
      );
    }
  }, []);

  // ─── Timer admin (live, pausable) ────────────────────────────────
  useEffect(() => {
    // 🎨 [Phase v13.54 hotfix TC2 — 2026-07-22] Format non-ambigu "Xh YYmin".
    // Uniformisation avec `useElapsedTime` + `MobileBottomNav` + `ChantierDuJour`.
    if (!isAdmin || !currentPunch?.punch_in_time) { setAdminElapsed(formatDureeLive(0)); return; }
    const pauseStart = currentPunch?.pause_start_time;
    const update = () => {
      const pausedMs = parseFloat(currentPunch?.heures_pause || 0) * 3600 * 1000;
      const activeEnd = pauseStart ? new Date(pauseStart).getTime() : Date.now();
      const diff = Math.floor((activeEnd - new Date(currentPunch.punch_in_time).getTime() - pausedMs) / 1000);
      setAdminElapsed(formatDureeLive(Math.max(0, diff)));
    };
    update();
    if (pauseStart) return; // timer figé si en pause
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [isAdmin, currentPunch]);

  // ─── Timer pause admin ──────────────────────────────────────────
  useEffect(() => {
    const pauseStart = currentPunch?.pause_start_time;
    if (!isAdmin || !pauseStart) { setPauseElapsed(''); return; }
    const update = () => {
      const diff = Math.floor((Date.now() - new Date(pauseStart).getTime()) / 1000);
      // 🎨 [Phase v13.54 — 2026-07-21] Format non-ambigu "8h 55min" (base 60).
      // Voir /app/memory/rca_v13_54_3_taches.md §Tâche 2.
      setPauseElapsed(formatDureeLive(diff));
    };
    update();
    const t = setInterval(update, 30000);
    return () => clearInterval(t);
  }, [isAdmin, currentPunch?.pause_start_time]);

  // ─── Synchro hors-ligne ─────────────────────────────────────────
  const syncOffline = async () => {
    if (syncRef.current) return;
    const queue = getQueue();
    if (!queue.length) return;
    syncRef.current = true;
    setIsSyncing(true);
    let ok = 0;
    const failed = [];
    for (const item of queue) {
      try {
        const endpoint = item.action === 'punch_in' ? '/api/punch/in' : '/api/punch/out';
        const res = await axios.post(`${API_URL}${endpoint}`, item.payload);
        if (res.status === 200) ok++;
        else failed.push(item);
      } catch { failed.push(item); }
    }
    saveQueue(failed);
    setPendingQueue(failed);
    setIsSyncing(false);
    syncRef.current = false;
    if (ok > 0) { toast.success(`${ok} pointage(s) synchronisé(s)`); loadData(); }
  };

  // ─── Punch IN ──────────────────────────────────────────────────
  //
  // 🆕 [2026-06-11] Flow avec pré-check « hors zone » :
  //   1. requestGPS (failsafe : si refusé → abort comme avant)
  //   2. Pré-check distance employé↔chantier (failsafe : tout échec = punch direct)
  //   3. Si hors_zone → dialog confirmation "Vous êtes à X m... Oui/Non"
  //   4. Sinon (ou « Oui ») → exécute le punch
  // Le code original (depuis « checkInspectionVendredi ») est intégralement
  // préservé dans `_executePunchInChantier(bon, gps)`.
  const _executePunchInChantier = async (bon, gps) => {
    const payload = {
      employee_id: user?.employee_id,
      contrat_id: bon.id,
      bon_travail_id: bon.id,
      type_punch: 'chantier',
      employee_nom: `${user.prenom || ''} ${user.nom || ''}`.trim(),
      latitude: gps.latitude,
      longitude: gps.longitude,
      // 🆕 [9.1] Traçabilité GPS pour détecter cache navigateur agressif
      gps_timestamp: gps.gps_timestamp,
      gps_accuracy: gps.gps_accuracy,
    };
    try {
      if (!isOnline) {
        const localPunch = { id: `local_${Date.now()}`, ...payload, punch_in_time: new Date().toISOString(), statut: 'en_cours', is_offline: true };
        setCurrentPunch(localPunch);
        saveCurrentPunch(localPunch);
        const q = getQueue();
        q.push({ action: 'punch_in', payload });
        saveQueue(q);
        setPendingQueue(q);
        toast.success('Punch enregistré localement (hors-ligne)');
        // Pop-up nacelle même en hors-ligne si chef d'équipe
        checkNacellePopup(bon, 'debut');
        return;
      }

      // ✅ Vérifier inspection vendredi avant de procéder au punch
      const inspectionRequise = await checkInspectionVendredi(bon);
      if (inspectionRequise) {
        setPunching(false);
        return; // Le modal d'inspection s'est ouvert — le punch attendra
      }
      const res = await axios.post(`${API_URL}/api/punch/in`, payload);
      const data = res.data;
      if (res.status !== 200) throw new Error(data.detail || 'Erreur punch');
      setCurrentPunch(data.punch);
      clearCurrentPunch();
      toast.success('Chantier commencé !');
      // ✅ Pop-up nacelle si nacelle prévue aujourd'hui — 🧱 Lot C (D2) : filtré par la clé RBAC
      //   `bon_equipements_location` dans checkNacellePopup (plus de rôle en dur).
      checkNacellePopup(bon, 'debut');
      loadData();
    } catch (e) {
      toast.error(e.message || 'Erreur lors du punch');
    } finally {
      setPunching(false);
    }
  };

  const handlePunchIn = async (bon) => {
    if (!user?.employee_id) { toast.error('Votre compte n\'est pas lié à une fiche employé'); return; }
    setPunching(true);

    // GPS obligatoire
    const { requestGPS } = await import('../lib/gpsUtils');
    const gps = await requestGPS({ required: true });
    if (!gps) {
      setPunching(false);
      return;
    }
    setLocation(gps);

    // 🆕 [2026-06-11] Pré-check distance employé↔chantier (FAILSAFE absolu)
    //   - Si hors-ligne, BT.id manquant, API timeout/erreur → on saute le check,
    //     le punch part comme avant (le backend re-vérifiera de toute façon).
    //   - Si hors zone → dialog confirmation, et on attend la décision user.
    if (isOnline && bon?.id) {
      try {
        const checkRes = await axios.post(
          `${API_URL}/api/punch/check-distance`,
          {
            bon_travail_id: bon.id,
            latitude: gps.latitude,
            longitude: gps.longitude,
          },
          { timeout: 3000 }
        );
        if (checkRes?.data?.hors_zone === true && checkRes?.data?.distance_m != null) {
          // Hors zone détecté → ouvre dialog et attend la décision.
          // setPunching(false) car les boutons Oui/Non gèrent eux-mêmes la suite.
          setHorsZoneConfirmIn({
            bon,
            gps,
            distance_m: checkRes.data.distance_m,
            isServiceCall: false,
          });
          setPunching(false);
          return;
        }
      } catch (_e) {
        // FAILSAFE : tout échec du check (timeout, 500, réseau) → punch direct.
        // Le check est purement informatif, jamais bloquant.
      }
    }

    // Dans la zone OU échec du check (failsafe) → punch normal.
    await _executePunchInChantier(bon, gps);
  };

  // 🔄 Phase A2.3 — Démarrer un punch sur un Service Call
  // Le backend dérive automatiquement bon_travail_id du SC + force type_punch="service_call"
  //
  // 🆕 [2026-06-11] Pré-check « hors zone » identique au punch chantier (FAILSAFE).
  const _executePunchInServiceCall = async (sc, gps) => {
    const payload = {
      employee_id: user.employee_id,
      employee_nom: `${user.prenom || ''} ${user.nom || ''}`.trim(),
      service_call_id: sc.id,            // ← le backend dérive le BT parent
      latitude: gps.latitude,
      longitude: gps.longitude,
      // 🆕 [9.1] Traçabilité GPS pour détecter cache navigateur agressif
      gps_timestamp: gps.gps_timestamp,
      gps_accuracy: gps.gps_accuracy,
    };
    try {
      const res = await axios.post(`${API_URL}/api/punch/in`, payload);
      if (res.status !== 200) throw new Error(res.data?.detail || 'Erreur punch SC');
      toast.success(`Service Call commencé : ${sc.client_nom || sc.description?.slice(0, 30)}`);
      loadData();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Erreur lors du punch SC';
      toast.error(msg);
    } finally {
      setPunching(false);
    }
  };

  const handlePunchInServiceCall = async (sc) => {
    if (!user?.employee_id) { toast.error('Votre compte n\'est pas lié à une fiche employé'); return; }
    if (currentPunch) {
      toast.warning('Un punch est déjà en cours — terminez-le avant de démarrer un Service Call.');
      return;
    }
    setPunching(true);

    // GPS obligatoire (zone héritée du BT parent / dossier)
    const { requestGPS } = await import('../lib/gpsUtils');
    const gps = await requestGPS({ required: true });
    if (!gps) {
      setPunching(false);
      return;
    }
    setLocation(gps);

    // 🆕 [2026-06-11] Pré-check distance (FAILSAFE — voir handlePunchIn).
    // Le BT parent du SC est nécessaire ; si absent, on saute le check.
    const btId = sc?.bon_travail_id;
    if (isOnline && btId) {
      try {
        const checkRes = await axios.post(
          `${API_URL}/api/punch/check-distance`,
          {
            bon_travail_id: btId,
            latitude: gps.latitude,
            longitude: gps.longitude,
          },
          { timeout: 3000 }
        );
        if (checkRes?.data?.hors_zone === true && checkRes?.data?.distance_m != null) {
          setHorsZoneConfirmIn({
            bon: null,
            sc,
            gps,
            distance_m: checkRes.data.distance_m,
            isServiceCall: true,
          });
          setPunching(false);
          return;
        }
      } catch (_e) {
        // FAILSAFE : tout échec → punch direct
      }
    }

    await _executePunchInServiceCall(sc, gps);
  };

  // 🔧 Bug #5 — Handler bascule entre 2 BT (ouvre modal de confirmation)
  const handlePunchSwitch = (bon) => {
    if (!currentPunch) {
      toast.warning("Aucun punch en cours — utilisez « Commencer » pour démarrer ce chantier.");
      return;
    }
    setSwitchTarget({ bon });
  };

  // 🔧 Bug #5 — Confirmation effective : appel atomique /punch/switch
  const confirmSwitch = async () => {
    if (!switchTarget?.bon || !currentPunch) return;
    const newBon = switchTarget.bon;
    const fromBonId = currentPunch.bon_travail_id || '';
    setSwitchTarget(null);
    setPunching(true);
    try {
      // GPS obligatoire
      const { requestGPS } = await import('../lib/gpsUtils');
      const gps = await requestGPS({ required: true });
      if (!gps) { setPunching(false); return; }

      const payload = {
        employee_id: user?.employee_id,
        employee_nom: `${user?.prenom || ''} ${user?.nom || ''}`.trim(),
        contrat_id: newBon.id,
        bon_travail_id: newBon.id,
        from_bon_travail_id: fromBonId,
        type_punch: newBon.est_interne ? 'interne' : 'chantier',
        latitude: gps.latitude,
        longitude: gps.longitude,
        adresse: gps.adresse || '',
        // 🆕 [9.1] Traçabilité GPS pour détecter cache navigateur agressif
        gps_timestamp: gps.gps_timestamp,
        gps_accuracy: gps.gps_accuracy,
        heures_pause: 0,
      };
      const res = await axios.post(`${API_URL}/api/punch/switch`, payload);
      if (res.status !== 200) throw new Error(res.data?.detail || 'Erreur bascule');
      const fromName = bons.find(b => b.id === fromBonId)?.client_nom || 'Job précédent';
      const toName = newBon.client_nom || newBon.nom_dossier || 'Nouveau chantier';
      toast.success(`Bascule réussie : ${fromName} → ${toName}`);
      setCurrentPunch(res.data?.new_punch || null);
      loadData();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Erreur lors de la bascule';
      toast.error(msg);
    } finally {
      setPunching(false);
    }
  };

  // ─── Punch OUT ─────────────────────────────────────────────────
  const handlePunchOut = async (bon, type) => {
    if (type === 'pause') {
      setPendingPunchOut({ bon, type });
      setShowPauseModal(true);
    } else {
      // Terrain employees: auto-deduct 30 min pause
      const autoDeductPause = !isAdmin ? 30 : 0;
      // 🆕 2026-05-23 — Si pause PM applicable (employé terrain + date ≥ 25 mai),
      // on ouvre d'abord la modal de déclaration avant d'enregistrer le punch.
      //
      // 🐛 [Hotfix 2026-06-11 Phase 49] L'éligibilité dépend du TOTAL des heures
      // travaillées du jour (seuil 7h35). Le useEffect au mount fetche au début
      // de journée quand l'employé n'a pas encore 7h35 → state stale à `false`
      // au punch-out de fin de journée → modale ne s'ouvrait JAMAIS.
      // Fix : re-fetch juste avant le check pour avoir l'éligibilité FRAÎCHE.
      //
      // 🆕 [Phase v13.60.5 — 2026-08-14] Défense en profondeur contre les zombies :
      //   - Retry avec backoff sur le re-fetch (2 tentatives).
      //   - Si tout échoue ET cache local "déjà répondu aujourd'hui" → laisser
      //     passer sans modale (pas de false positive).
      //   - Sinon → toast rouge bloquant qui interrompt le dépunch et demande
      //     à l'employé de retenter dans une meilleure zone réseau.
      // Cf. /app/memory/rca_v13_60_5_pause_pm.md §4.2.
      let applicableNow = pausePmApplicable;
      if (!isAdmin) {
        const data = await fetchPausePmApplicableWithRetry(authHeaders);
        if (data !== null) {
          applicableNow = !!data.applicable;
          setPausePmApplicable(applicableNow);
        } else {
          // Le re-fetch a échoué même après retries.
          if (pausePmLocalCache.hasDeclared(user?.employee_id)) {
            // L'employé a déjà répondu OUI/NON aujourd'hui sur ce navigateur.
            // On laisse le dépunch procéder normalement — le backend ne lui
            // reposera pas la question (`already_declared_today` protection).
            applicableNow = false;
            // 🔎 [BLOC 2 v13.62] Instrumentation — le cache local a shortcut la modale.
            try {
              reportClientEvent('pause_pm_cache_lookup', 'info', {
                hit: true,
                shortcut_reason: 'retry_failed_but_cache_declared',
                employee_id_present: !!user?.employee_id,
              });
            } catch { /* jamais throw */ }
          } else {
            // Aucune trace locale d'une réponse → risque zombie critique.
            // On BLOQUE le dépunch et on invite l'employé à réessayer.
            // 🔎 [BLOC 2 v13.62] Instrumentation — pas de cache, on va bloquer.
            try {
              reportClientEvent('pause_pm_cache_lookup', 'warning', {
                hit: false,
                shortcut_reason: 'retry_failed_no_cache_toast_blocking',
                employee_id_present: !!user?.employee_id,
              });
            } catch { /* jamais throw */ }
            toast.error(
              'Connexion instable — impossible de vérifier ta pause de l\'après-midi. '
              + 'Approche-toi d\'une meilleure zone Wi-Fi ou 4G et retente ton dépunch. '
              + 'Ton pointage sera enregistré une fois la vérification faite.',
              { duration: 10000 }
            );
            return;
          }
        }
      }
      if (applicableNow && !isAdmin) {
        setPausePmModal({ bon, step: 'question', pauseMin: autoDeductPause });
        return;
      }
      confirmPunchOut(bon, autoDeductPause);
    }
  };

  const confirmPunchOut = async (bon, pauseMin, pausePmPrise = null) => {
    if (!currentPunch) return;
    setPunching(true);
    setShowPauseModal(false);

    // GPS obligatoire au punch out aussi
    const { requestGPS } = await import('../lib/gpsUtils');
    const gps = await requestGPS({ required: true });
    if (!gps) {
      setPunching(false);
      return;
    }

    // ✅ FIX: Utiliser la pause réellement prise (enregistrée via pause_start/pause_end)
    // Au lieu de la valeur sélectionnée manuellement dans le modal
    const pauseReellePrise = parseFloat(currentPunch?.heures_pause || 0);
    
    const payload = {
      punch_id: currentPunch.id,
      latitude: gps.latitude,
      longitude: gps.longitude,
      // 🆕 [9.1] Traçabilité GPS pour détecter cache navigateur agressif
      gps_timestamp: gps.gps_timestamp,
      gps_accuracy: gps.gps_accuracy,
      heures_pause: pauseReellePrise > 0 ? pauseReellePrise : (pauseMin / 60),
    };
    // 🆕 2026-05-23 — Déclaration pause PM transmise au backend (true/false/null)
    if (pausePmPrise !== null && pausePmPrise !== undefined) {
      payload.pause_pm_prise = !!pausePmPrise;
      // 🆕 [Phase v13.60.5] Marquer le cache local — l'employé a répondu
      // aujourd'hui, on ne redemandera plus même si /pause-pm-applicable
      // devient injoignable plus tard dans la journée.
      pausePmLocalCache.markDeclared(user?.employee_id);
    }
    try {
      if (!isOnline) {
        setCurrentPunch(null);
        clearCurrentPunch();
        const q = getQueue();
        q.push({ action: 'punch_out', payload });
        saveQueue(q);
        setPendingQueue(q);
        toast.success('Fin de journée enregistrée localement (hors-ligne)');
        return;
      }
      const res = await axios.post(`${API_URL}/api/punch/out`, payload);
      const data = res.data;
      if (res.status !== 200) throw new Error(data.detail || 'Erreur punch out');
      setCurrentPunch(null);
      toast.success(pauseMin > 0 ? 'Pause enregistrée, bon retour !' : 'Journée terminée — bravo !');
      loadData();

      // 🆕 [2026-06-11] Toast informatif si dépunch hors zone (FAILSAFE silencieux).
      // Exécuté APRÈS que le punch soit enregistré → ne le bloque jamais.
      // Tout échec du check (timeout/erreur/coords manquantes) est silencieux.
      if (isOnline && bon?.id) {
        try {
          const checkRes = await axios.post(
            `${API_URL}/api/punch/check-distance`,
            {
              bon_travail_id: bon.id,
              latitude: gps.latitude,
              longitude: gps.longitude,
            },
            { timeout: 3000 }
          );
          if (checkRes?.data?.hors_zone === true && checkRes?.data?.distance_m != null) {
            const m = Math.round(checkRes.data.distance_m);
            toast.warning(
              `Attention, vous vous êtes dépunché hors zone à ${m} m du chantier.`,
              { duration: 7000 }
            );
          }
        } catch (_e) {
          // Silence : le punch est déjà enregistré, on ne dérange pas l'employé.
        }
      }
    } catch (e) {
      toast.error(e.message || 'Erreur lors du punch out');
    } finally {
      setPunching(false);
    }
  };

  // ─── Punch ADMIN (sans bon de travail) ─────────────────────────
  const handleAdminPunchIn = async () => {
    setPunching(true);

    // GPS obligatoire pour le punch admin aussi
    const { requestGPS } = await import('../lib/gpsUtils');
    const gps = await requestGPS({ required: true });
    if (!gps) { setPunching(false); return; }
    setLocation(gps);

    const payload = {
      employee_id: user?.employee_id,
      contrat_id: null,
      bon_travail_id: null,
      type_punch: 'admin',
      employee_nom: `${user?.prenom || ''} ${user?.nom || ''}`.trim(),
      latitude: gps.latitude,
      longitude: gps.longitude,
      // 🆕 [9.1] Traçabilité GPS pour détecter cache navigateur agressif
      gps_timestamp: gps.gps_timestamp,
      gps_accuracy: gps.gps_accuracy,
    };
    try {
      if (!isOnline) {
        const localPunch = { id: `local_${Date.now()}`, ...payload, punch_in_time: new Date().toISOString(), statut: 'en_cours', is_offline: true };
        setCurrentPunch(localPunch);
        saveCurrentPunch(localPunch);
        const q = getQueue();
        q.push({ action: 'punch_in', payload });
        saveQueue(q);
        setPendingQueue(q);
        toast.success('Journée démarrée localement (hors-ligne)');
        return;
      }
      const res = await axios.post(`${API_URL}/api/punch/in`, payload);
      const data = res.data;
      if (res.status !== 200) throw new Error(data.detail || 'Erreur punch');
      setCurrentPunch(data.punch);
      clearCurrentPunch();
      toast.success('Bonne journée !');
      loadData();
    } catch (e) {
      toast.error(e.message || 'Erreur lors du démarrage');
    } finally {
      setPunching(false);
    }
  };

  // 🆕 2026-05-14 — Rebasculer depuis un BT vers les heures admin (séquentiel : OUT BT + IN admin).
  //   L'inverse (admin → BT) est déjà géré par le bouton "Basculer sur ce chantier"
  //   de chaque BonCard via handlePunchSwitch.
  const handleRebasculerAdmin = async () => {
    if (!currentPunch || !currentPunch.bon_travail_id) {
      toast.warning("Aucun BT actif à rebasculer.");
      return;
    }
    setPunching(true);
    try {
      const { requestGPS } = await import('../lib/gpsUtils');
      const gps = await requestGPS({ required: true });
      if (!gps) { setPunching(false); return; }

      // 1. Punch OUT du BT actuel
      const outRes = await axios.post(`${API_URL}/api/punch/out`, {
        punch_id: currentPunch.id,
        latitude: gps.latitude,
        longitude: gps.longitude,
        adresse: gps.adresse || '',
        // 🆕 [9.1] Traçabilité GPS pour détecter cache navigateur agressif
        gps_timestamp: gps.gps_timestamp,
        gps_accuracy: gps.gps_accuracy,
        heures_pause: 0,
      });
      if (outRes.status !== 200) throw new Error(outRes.data?.detail || 'Erreur punch out BT');

      // 2. Punch IN en mode admin (sans BT)
      const inRes = await axios.post(`${API_URL}/api/punch/in`, {
        employee_id: user?.employee_id,
        contrat_id: null,
        bon_travail_id: null,
        type_punch: 'admin',
        employee_nom: `${user?.prenom || ''} ${user?.nom || ''}`.trim(),
        latitude: gps.latitude,
        longitude: gps.longitude,
        adresse: gps.adresse || '',
        // 🆕 [9.1] Traçabilité GPS pour détecter cache navigateur agressif
        gps_timestamp: gps.gps_timestamp,
        gps_accuracy: gps.gps_accuracy,
      });
      if (inRes.status !== 200) throw new Error(inRes.data?.detail || 'Erreur punch in admin');

      setCurrentPunch(inRes.data?.punch || null);
      toast.success('Rebasculé en heures admin ✓');
      loadData();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Erreur lors de la bascule admin';
      toast.error(msg);
    } finally {
      setPunching(false);
    }
  };

  const handleAdminPunchOut = () => {
    setPendingPunchOut({ bon: null, type: 'fin' });
    // End of day: punch out directly (no pause modal for admins)
    confirmPunchOut(null, 0);
  };

  // ─── Admin Pause START ──────────────────────────────────────────
  const handleAdminStartPause = async () => {
    if (!currentPunch?.id) return;
    setPauseLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/punch/pause/start`, { punch_id: currentPunch.id });
      if (res.status === 200) {
        toast.success('Pause commencée — bon repos !');
        loadData();
      } else {
        const err = res.data;
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setPauseLoading(false); }
  };

  // ─── Admin Pause END ────────────────────────────────────────────
  const handleAdminEndPause = async () => {
    if (!currentPunch?.id) return;
    setPauseLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/punch/pause/end`, { punch_id: currentPunch.id });
      if (res.status === 200) {
        const data = res.data;
        toast.success(`Pause terminée (${Math.round(data.duree_pause_minutes || 0)} min) — bon retour !`);
        loadData();
      } else {
        const err = res.data;
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setPauseLoading(false); }
  };

  // ─── Calcul heures du jour ──────────────────────────────────────
  const heuresToday = todayPunches.reduce((sum, p) => sum + (p.heures_nettes || 0), 0);
  const today = new Date().toLocaleDateString('fr-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      <p className="text-gray-500">{isAdmin ? 'Chargement...' : 'Chargement de vos chantiers...'}</p>
    </div>
  );

  return (
    <div className="max-w-lg mx-auto px-4 py-6 space-y-5" data-testid="pointage-terrain">

      {/* ── Modal Inspection Vendredi ── */}
      <Dialog open={!!inspectionModal} onOpenChange={() => {}}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-indigo-700">🔍 Inspection hebdomadaire obligatoire</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              C'est vendredi — votre inspection d'unité est requise avant de vous puncher.
              <strong className="text-indigo-700"> Le temps de l'inspection sera inclus dans votre punch.</strong>
            </p>
            {(inspectionModal?.outils || []).map(o => {
              const statut = inspectionModal?.inspection?.[o.id] || 'present';
              return (
                <div key={o.id} className="flex items-center gap-2 p-2.5 rounded-lg bg-white border border-slate-200">
                  <span className="flex-1 text-sm font-medium">{o.nom} <span className="text-slate-400">×{o.quantite}</span></span>
                  <div className="flex gap-1">
                    {[
                      { key: 'present', label: '✅', title: 'Présent' },
                      { key: 'manquant', label: '❓', title: 'Manquant' },
                      { key: 'brise', label: '🔴', title: 'Brisé' },
                    ].map(s => (
                      <button key={s.key} title={s.title}
                        onClick={() => setInspectionModal(prev => ({
                          ...prev, inspection: { ...prev.inspection, [o.id]: s.key }
                        }))}
                        className={`text-lg px-1.5 py-0.5 rounded transition-transform ${statut === s.key ? 'scale-125 bg-slate-100' : 'opacity-50 hover:opacity-80'}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
            <Button
              onClick={async () => {
                const resultats = (inspectionModal?.outils || []).map(o => ({
                  outil_id: o.id, nom: o.nom,
                  statut: inspectionModal?.inspection?.[o.id] || 'present',
                }));
                try {
                  await axios.post(`${API_URL}/api/unites-rangement/${inspectionModal.unite.id}/inspections`, {
                    resultats,
                    chef_id: user?.employee_id,
                    chef_nom: `${user?.prenom || ''} ${user?.nom || ''}`.trim(),
                  });
                  toast.success('✅ Inspection enregistrée — punch en cours...');
                  const bon = pendingPunchBon;
                  setInspectionModal(null);
                  setPendingPunchBon(null);
                  // Continuer le punch
                  if (bon) await handlePunchIn(bon);
                } catch { toast.error('Erreur enregistrement inspection'); }
              }}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="btn-submit-inspection-punch"
            >
              Enregistrer l'inspection et se puncher
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Pop-up Nacelle ── */}
      <Dialog open={!!nacellePopup && canEquipements} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-cyan-700">
              🏗 Nacelle — {nacellePopup?.type === 'debut' ? 'Début utilisation' : 'Fin utilisation'}
            </DialogTitle>
          </DialogHeader>
          {nacellePopup?.type === 'debut' && nacellePopup?.step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-700">Vous avez une nacelle prévue aujourd'hui chez <strong>{nacellePopup?.bon?.client_nom || 'ce client'}</strong>.</p>
              <p className="text-sm font-semibold text-cyan-800">Avez-vous bien la nacelle avec vous pour commencer?</p>
              <div className="flex gap-2">
                <Button onClick={() => confirmNacellePopup('oui')} className="flex-1 bg-cyan-600 hover:bg-cyan-700">Oui</Button>
                <Button onClick={() => confirmNacellePopup('non')} variant="outline" className="flex-1">Non / Problème</Button>
              </div>
            </div>
          )}
          {nacellePopup?.type === 'debut' && nacellePopup?.step === 2 && (
            <div className="space-y-4">
              <p className="text-sm font-semibold text-cyan-800">Confirmation finale — vous confirmez avoir la nacelle?</p>
              <div className="flex gap-2">
                <Button onClick={() => confirmNacellePopup('oui')} className="flex-1 bg-cyan-600 hover:bg-cyan-700">Oui, confirmer</Button>
                <Button onClick={() => setNacellePopup(null)} variant="outline" className="flex-1">Annuler</Button>
              </div>
            </div>
          )}
          {nacellePopup?.type === 'fin' && nacellePopup?.step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-700">C'est le <strong>dernier jour prévu</strong> avec la nacelle chez <strong>{nacellePopup?.bon?.client_nom || 'ce client'}</strong>.</p>
              <p className="text-sm font-semibold text-cyan-800">Avez-vous terminé avec la nacelle?</p>
              <div className="flex flex-col gap-2">
                <Button onClick={() => confirmNacellePopup('oui')} className="bg-emerald-600 hover:bg-emerald-700">Oui — nacelle retournée au bureau</Button>
                <Button onClick={() => confirmNacellePopup('plus')} variant="outline" className="border-amber-400 text-amber-700">J'ai besoin d'1 journée de plus</Button>
                <Button onClick={() => confirmNacellePopup('laisse')} variant="outline" className="border-blue-400 text-blue-700">Je la laisse chez le client</Button>
              </div>
            </div>
          )}
          {nacellePopup?.type === 'fin' && nacellePopup?.step === 2 && (
            <div className="space-y-4">
              <p className="text-sm font-semibold text-cyan-800">Confirmation finale?</p>
              <div className="flex gap-2">
                <Button onClick={() => confirmNacellePopup('confirmed')} className="flex-1 bg-emerald-600 hover:bg-emerald-700">Confirmer</Button>
                <Button onClick={() => setNacellePopup(null)} variant="outline" className="flex-1">Retour</Button>
              </div>
            </div>
          )}
          {/* Pop-up montage veille */}
          {nacellePopup?.type === 'montage_veille' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-700">
                Rappel : demain c'est la journée de montage de <strong>{nacellePopup?.eq?.equipement_nom || 'l\'équipement'}</strong> chez <strong>{nacellePopup?.bon?.client_nom || 'ce client'}</strong>.
              </p>
              <p className="text-sm font-semibold text-blue-800">Prévoyez de partir avec la remorque demain matin.</p>
              {nacellePopup.step === 1 && (
                <Button onClick={() => setNacellePopup({...nacellePopup, step: 2})} className="w-full bg-blue-600 hover:bg-blue-700">Compris ✓</Button>
              )}
              {nacellePopup.step === 2 && (
                <div className="flex gap-2">
                  <Button onClick={() => { setNacellePopup(null); toast.success('Rappel confirmé'); }} className="flex-1 bg-blue-600 hover:bg-blue-700">Confirmer</Button>
                  <Button onClick={() => setNacellePopup(null)} variant="outline" className="flex-1">Retour</Button>
                </div>
              )}
            </div>
          )}
          {/* Pop-ups démontage échafauds */}
          {nacellePopup?.type === 'demontage_veille' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-700">
                Rappel : demain c'est la journée de démontage des échafauds chez <strong>{nacellePopup?.bon?.client_nom || 'ce client'}</strong>.
              </p>
              <p className="text-sm font-semibold text-orange-800">Prévoyez de partir avec la remorque demain matin.</p>
              {nacellePopup.step === 1 && (
                <Button onClick={() => setNacellePopup({...nacellePopup, step: 2})} className="w-full bg-orange-600 hover:bg-orange-700">Compris ✓</Button>
              )}
              {nacellePopup.step === 2 && (
                <div className="flex gap-2">
                  <Button onClick={() => { setNacellePopup(null); toast.success('Rappel confirmé'); }} className="flex-1 bg-orange-600 hover:bg-orange-700">Confirmer</Button>
                  <Button onClick={() => setNacellePopup(null)} variant="outline" className="flex-1">Retour</Button>
                </div>
              )}
            </div>
          )}
          {nacellePopup?.type === 'demontage_matin' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-700">Aujourd'hui : démontage des échafauds chez <strong>{nacellePopup?.bon?.client_nom}</strong>.</p>
              <p className="text-sm font-semibold text-orange-800">Avez-vous la remorque d'échafauds avec vous ce matin?</p>
              {nacellePopup.step === 1 && (
                <div className="flex gap-2">
                  <Button onClick={() => setNacellePopup({...nacellePopup, step: 2})} className="flex-1 bg-orange-600 hover:bg-orange-700">Oui, je l'ai</Button>
                  <Button onClick={() => setNacellePopup(null)} variant="outline" className="flex-1">Non / Problème</Button>
                </div>
              )}
              {nacellePopup.step === 2 && (
                <div className="flex gap-2">
                  <Button onClick={() => { setNacellePopup(null); toast.success('Confirmé ✓'); }} className="flex-1 bg-emerald-600 hover:bg-emerald-700">Confirmer</Button>
                  <Button onClick={() => setNacellePopup(null)} variant="outline" className="flex-1">Retour</Button>
                </div>
              )}
            </div>
          )}
          {nacellePopup?.type === 'demontage_soir' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-700">Fin de journée — démontage chez <strong>{nacellePopup?.bon?.client_nom}</strong>.</p>
              <p className="text-sm font-semibold text-orange-800">La remorque d'échafauds est-elle retournée au bureau?</p>
              {nacellePopup.step === 1 && (
                <div className="flex flex-col gap-2">
                  <Button onClick={() => setNacellePopup({...nacellePopup, step: 2})} className="bg-emerald-600 hover:bg-emerald-700">Oui, retournée au bureau</Button>
                  <Button onClick={() => setNacellePopup(null)} variant="outline" className="text-amber-700 border-amber-400">Non, pas encore</Button>
                </div>
              )}
              {nacellePopup.step === 2 && (
                <div className="flex gap-2">
                  <Button onClick={async () => {
                    try {
                      await axios.post(`${API_URL}/api/crm/bons-travail/${nacellePopup.bon.id}/terminer-equipement`, { type_equipement: 'echafaud' });
                      toast.success('✅ Remorque confirmée retournée au bureau');
                    } catch { toast.error('Erreur'); }
                    setNacellePopup(null);
                  }} className="flex-1 bg-emerald-600 hover:bg-emerald-700">Confirmer</Button>
                  <Button onClick={() => setNacellePopup(null)} variant="outline" className="flex-1">Retour</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bannière hors-ligne */}
      {!isOnline && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 flex items-center gap-2 text-amber-800">
          <WifiOff className="w-5 h-5 shrink-0" />
          <p className="text-sm font-medium">Mode hors-ligne — les pointages seront synchronisés à la reconnexion</p>
        </div>
      )}

      {/* Bannière synchro */}
      {isOnline && pendingQueue.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-blue-800">
            {isSyncing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CloudUpload className="w-4 h-4" />}
            <p className="text-sm">{isSyncing ? 'Synchronisation...' : `${pendingQueue.length} pointage(s) en attente`}</p>
          </div>
          {!isSyncing && (
            <Button size="sm" variant="outline" className="border-blue-300 text-blue-700" onClick={syncOffline}>
              Synchroniser
            </Button>
          )}
        </div>
      )}

      {/* Header */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Bonjour, {user?.prenom || 'Employé'} 👋
            </h1>
            <p className="text-sm text-gray-500 capitalize mt-0.5">{today}</p>
          </div>
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-amber-500'}`} />
            <span className="text-xs text-gray-500">{isOnline ? 'En ligne' : 'Hors-ligne'}</span>
          </div>
        </div>

        {/* Résumé journée */}
        {heuresToday > 0 && (
          <div className="mt-3 bg-blue-50 rounded-xl p-3 flex items-center gap-3">
            <Clock className="w-5 h-5 text-blue-600 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-blue-800">{heuresToday.toFixed(2)} h travaillées aujourd'hui</p>
              <p className="text-xs text-blue-600">{todayPunches.length} session{todayPunches.length > 1 ? 's' : ''} de pointage</p>
            </div>
          </div>
        )}
      </div>

      {/* Liste des bons du jour (terrain) OU Punch admin */}
      <div>
        {/* 🆕 2026-05-07 — BTs en renfort pour CP/adjoint admin (cédulés ponctuellement)
            Apparaît AU-DESSUS du bloc admin pour qu'ils puissent puncher dessus. */}
        {isAdmin && btsRenfort.length > 0 && (
          <div className="mb-5" data-testid="section-bts-renfort-pointage">
            <h2 className="text-sm font-semibold text-amber-700 uppercase tracking-wide mb-3 flex items-center gap-2">
              <LifeBuoy className="w-4 h-4" />
              Mes BTs en renfort aujourd'hui ({btsRenfort.length})
            </h2>
            <div className="space-y-3">
              {btsRenfort.map(bon => (
                <BonCard
                  key={bon.id}
                  bon={bon}
                  currentPunch={currentPunch}
                  employeeId={user?.employee_id}
                  employeeNom={`${user?.prenom || ''} ${user?.nom || ''}`.trim()}
                  location={location}
                  onPunchIn={handlePunchIn}
                  onPunchOut={handlePunchOut}
                  onPunchSwitch={handlePunchSwitch}
                  loading={punching}
                />
              ))}
            </div>
          </div>
        )}

        {isAdmin ? (
          /* ─── Mode Admin / Propriétaire ─── */
          <div>
            {/* 🆕 2026-05-14 — Si l'admin est punché sur un BT, on cache la carte
                "Ma journée de travail" pour ne pas afficher 2 compteurs identiques.
                À la place : un bandeau clair indiquant qu'on est sur un BT, avec
                un bouton "Rebasculer en heures admin" pour revenir au mode admin. */}
            {currentPunch && currentPunch.bon_travail_id && currentPunch.type_punch !== 'admin' ? (
              <div data-testid="admin-on-bt-banner" className="rounded-xl border-2 border-blue-300 bg-gradient-to-br from-blue-50 to-indigo-50 p-4 mb-5">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 mt-0.5">
                    <Briefcase className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-blue-900">
                      Vous êtes pointé sur un BT
                    </p>
                    <p className="text-xs text-blue-700 mt-0.5">
                      Vos heures s'accumulent sur ce chantier. Utilisez la carte ci-dessous pour terminer ou rebasculer en heures admin.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="w-full mt-3 border-amber-400 text-amber-700 hover:bg-amber-50 h-11 text-sm font-semibold"
                  onClick={handleRebasculerAdmin}
                  disabled={punching}
                  data-testid="admin-rebasculer-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {punching ? 'Bascule en cours...' : 'Rebasculer en heures admin'}
                </Button>
              </div>
            ) : (
            <>
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Ma journée de travail
            </h2>
            {!user?.employee_id ? (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="py-8 text-center">
                  <p className="text-amber-700 font-medium">Votre compte n'est pas lié à une fiche employé</p>
                  <p className="text-sm text-amber-600 mt-1">Contactez un administrateur pour lier votre compte</p>
                </CardContent>
              </Card>
            ) : currentPunch ? (
              isProprietaire ? (
                /* ─── Propriétaire: ancien comportement (Terminer + Pause avec durée) ─── */
                <Card className="border-2 border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 shadow-lg">
                  <CardContent className="p-4">
                    <div className="text-center mb-4">
                      <Badge className="bg-green-500 text-white animate-pulse mb-3">En cours</Badge>
                      <p className="text-4xl font-mono font-bold text-green-700 tracking-widest" data-testid="active-timer-admin">
                        {adminElapsed}
                      </p>
                      <p className="text-sm text-green-600 mt-1">Journée en cours depuis {new Date(currentPunch.punch_in_time).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white h-12 text-base font-semibold"
                        onClick={() => confirmPunchOut(null, 0)}
                        disabled={punching}
                        data-testid="admin-punch-out"
                      >
                        <Square className="w-5 h-5 mr-2" />
                        Terminer ma journée
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 border-amber-400 text-amber-700 hover:bg-amber-50 h-12 text-base font-semibold"
                        onClick={() => { setPendingPunchOut({ bon: null, type: 'pause' }); setShowPauseModal(true); }}
                        disabled={punching}
                        data-testid="admin-pause"
                      >
                        <Coffee className="w-5 h-5 mr-2" />
                        Pause
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                /* ─── Admin non-propriétaire: Commencer/Terminer ma pause en temps réel ─── */
                <Card className={`border-2 shadow-lg ${currentPunch.pause_start_time
                  ? 'border-amber-400 bg-gradient-to-br from-amber-50 to-yellow-50'
                  : 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50'
                }`}>
                  <CardContent className="p-4">
                    <div className="text-center mb-4">
                      <Badge className={`${currentPunch.pause_start_time ? 'bg-amber-500' : 'bg-green-500'} text-white animate-pulse mb-3`}>
                        {currentPunch.pause_start_time ? 'En pause' : 'En cours'}
                      </Badge>
                      <p className={`text-4xl font-mono font-bold tracking-widest ${currentPunch.pause_start_time ? 'text-amber-600' : 'text-green-700'}`} data-testid="active-timer-admin">
                        {currentPunch.pause_start_time ? pauseElapsed || '0 min' : adminElapsed}
                      </p>
                      <p className={`text-sm mt-1 ${currentPunch.pause_start_time ? 'text-amber-600' : 'text-green-600'}`}>
                        {currentPunch.pause_start_time
                          ? `En pause depuis ${new Date(currentPunch.pause_start_time).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}`
                          : `Journée en cours depuis ${new Date(currentPunch.punch_in_time).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}`
                        }
                      </p>
                    </div>

                    {currentPunch.pause_start_time ? (
                      /* ── EN PAUSE → bouton Reprendre ── */
                      <Button
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 text-base font-semibold"
                        onClick={handleAdminEndPause}
                        disabled={pauseLoading || punching}
                        data-testid="admin-end-pause"
                      >
                        <Play className="w-5 h-5 mr-2" />
                        {pauseLoading ? 'Chargement...' : 'Terminer ma pause'}
                      </Button>
                    ) : (
                      /* ── EN SERVICE → Pause + Terminer (stack vertical sur mobile pour éviter l'overflow du texte) ── */
                      <div className="flex flex-col sm:flex-row gap-2 w-full">
                        <Button
                          className="flex-1 min-w-0 bg-red-600 hover:bg-red-700 text-white h-12 text-base font-semibold"
                          onClick={handleAdminPunchOut}
                          disabled={punching || pauseLoading}
                          data-testid="admin-punch-out"
                        >
                          <Square className="w-5 h-5 mr-2 shrink-0" />
                          <span className="truncate">Terminer ma journée</span>
                        </Button>
                        <Button
                          variant="outline"
                          className="flex-1 min-w-0 border-amber-400 text-amber-700 hover:bg-amber-50 h-12 text-base font-semibold"
                          onClick={handleAdminStartPause}
                          disabled={punching || pauseLoading}
                          data-testid="admin-start-pause"
                        >
                          <Coffee className="w-5 h-5 mr-2 shrink-0" />
                          <span className="truncate">Commencer ma pause</span>
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )
            ) : (
              <Card className="border-dashed border-gray-300">
                <CardContent className="py-10 text-center">
                  <Clock className="w-12 h-12 text-blue-400 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium mb-1">Prêt à commencer votre journée ?</p>
                  <p className="text-sm text-gray-400 mb-4">Vos heures seront enregistrées sans bon de travail</p>
                  <Button
                    className="bg-green-600 hover:bg-green-700 text-white h-12 px-8 text-base font-semibold"
                    onClick={handleAdminPunchIn}
                    disabled={punching}
                    data-testid="admin-punch-in"
                  >
                    <Play className="w-5 h-5 mr-2" />
                    Commencer ma journée
                  </Button>
                </CardContent>
              </Card>
            )}
            </>
            )}
          </div>
        ) : (
          /* ─── Mode Terrain (peintre, chef d'équipe, sous-traitant) ─── */
          <div>
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Chantiers du jour
            </h2>

            {/* Carte spéciale : route de lavage en cours (chrono défilant) */}
            {currentPunch && typeof currentPunch.contrat_id === 'string' && currentPunch.contrat_id.startsWith('lavage-') && (
              <div className="mb-3">
                <RouteLavageCard currentPunch={currentPunch} />
              </div>
            )}

            {bons.length === 0 ? (
              // Si une route de lavage est active, on n'affiche pas le message vide (déjà remplacé par la carte lavage)
              (!(currentPunch && typeof currentPunch.contrat_id === 'string' && currentPunch.contrat_id.startsWith('lavage-'))) && (
                <Card className="border-dashed border-gray-300">
                  <CardContent className="py-10 text-center">
                    <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">Aucun chantier planifié pour aujourd'hui</p>
                    <p className="text-sm text-gray-400 mt-1">Vos bons de travail apparaissent ici lorsqu'une date vous est assignée</p>
                    <Button variant="ghost" size="sm" className="mt-3 text-blue-600" onClick={loadData}>
                      <RefreshCw className="w-4 h-4 mr-1" /> Rafraîchir
                    </Button>
                  </CardContent>
                </Card>
              )
            ) : (
              <div className="space-y-3">
                {bons.map(bon => (
                  <BonCard
                    key={bon.id}
                    bon={bon}
                    currentPunch={currentPunch}
                    employeeId={user?.employee_id}
                    employeeNom={`${user?.prenom || ''} ${user?.nom || ''}`.trim()}
                    location={location}
                    onPunchIn={handlePunchIn}
                    onPunchOut={handlePunchOut}
                    onPunchSwitch={handlePunchSwitch}
                    loading={punching}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 🔄 Phase A2.3 — Service Calls assignés aujourd'hui */}
      {!isAdmin && serviceCalls && serviceCalls.length > 0 && (
        <div data-testid="section-service-calls">
          <h2 className="text-sm font-semibold text-orange-700 uppercase tracking-wide mb-2 flex items-center gap-2">
            <Wrench className="w-4 h-4 text-orange-600" />
            Mes Service Calls aujourd'hui ({serviceCalls.length})
          </h2>
          <div className="space-y-2">
            {serviceCalls.map(sc => {
              const isPunchedHere = currentPunch?.service_call_id === sc.id && currentPunch?.statut === 'en_cours';
              const otherPunchActive = !!currentPunch && !isPunchedHere;
              return (
                <Card
                  key={sc.id}
                  className={`border-2 ${isPunchedHere ? 'border-green-400 bg-green-50' : 'border-orange-300 bg-orange-50'}`}
                  data-testid={`sc-card-${sc.id}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <Badge className="bg-orange-100 text-orange-800 border-orange-300 text-[10px]">
                            🔧 Service Call
                          </Badge>
                          <span className="text-sm font-bold text-slate-800 truncate">
                            {sc.client_nom || 'Service Call'}
                          </span>
                        </div>
                        {sc.bon_travail_nom && (
                          <p className="text-xs text-slate-500 truncate">
                            BT parent : <span className="font-medium">{sc.bon_travail_nom}</span>
                          </p>
                        )}
                        {sc.description && (
                          <p className="text-xs text-slate-700 mt-1 line-clamp-2">{sc.description}</p>
                        )}
                        {sc.adresse_travaux && (
                          <p className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {sc.adresse_travaux}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2">
                      {isPunchedHere ? (
                        <Button
                          onClick={() => handlePunchOut({ id: sc.bon_travail_id }, 'fin')}
                          disabled={punching}
                          className="bg-red-600 hover:bg-red-700 text-white flex-1"
                          data-testid={`sc-punch-out-${sc.id}`}
                        >
                          <LogOutIcon className="w-4 h-4 mr-1" /> Terminer le SC
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handlePunchInServiceCall(sc)}
                          disabled={punching || otherPunchActive}
                          className="bg-orange-600 hover:bg-orange-700 text-white flex-1"
                          data-testid={`sc-punch-in-${sc.id}`}
                          title={otherPunchActive ? 'Termine ton punch en cours avant de démarrer un SC' : ''}
                        >
                          <Wrench className="w-4 h-4 mr-1" /> Commencer le SC
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Historique du jour */}
      {todayPunches.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            Historique du jour
          </h2>
          <Card>
            <CardContent className="p-3 divide-y divide-gray-100">
              {todayPunches.map((p, i) => (
                <div key={p.id || p._id || i} className="py-2 flex justify-between text-sm">
                  <span className="text-gray-600">
                    {new Date(p.punch_in_time).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                    {p.punch_out_time && ` → ${new Date(p.punch_out_time).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}`}
                    {p.pause_minutes > 0 && <span className="text-amber-600 ml-1">(-{p.pause_minutes} min pause)</span>}
                  </span>
                  <span className={`font-semibold ${p.statut === 'en_cours' ? 'text-green-600' : 'text-gray-800'}`}>
                    {p.statut === 'en_cours' ? 'En cours' : `${(p.heures_nettes || 0).toFixed(2)} h`}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* 🔧 Bug #5 — Modal de confirmation de bascule entre BT */}
      <Dialog open={!!switchTarget} onOpenChange={(open) => { if (!open) setSwitchTarget(null); }}>
        <DialogContent className="max-w-sm" data-testid="switch-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-600" /> Basculer de chantier ?
            </DialogTitle>
          </DialogHeader>
          {switchTarget && (
            <div className="space-y-3 text-sm">
              <p className="text-gray-700">
                Tu vas <strong>terminer</strong> ton punch en cours et <strong>commencer</strong> ce chantier en un seul clic.
              </p>
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="text-red-600 font-bold text-lg leading-none">×</span>
                  <div>
                    <p className="text-xs text-gray-500">Job en cours (sera terminée)</p>
                    <p className="font-semibold text-gray-800">
                      {(bons.find(b => b.id === currentPunch?.bon_travail_id) || {}).client_nom
                        || currentPunch?.contrat_nom
                        || 'Job en cours'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-600 font-bold text-lg leading-none">→</span>
                  <div>
                    <p className="text-xs text-gray-500">Nouvelle job</p>
                    <p className="font-semibold text-gray-800">
                      {switchTarget.bon.client_nom || switchTarget.bon.nom_dossier}
                    </p>
                    {switchTarget.bon.heure_debut && (
                      <p className="text-xs text-gray-500">⏰ {switchTarget.bon.heure_debut}</p>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Les heures de la job en cours seront calculées automatiquement.
              </p>
            </div>
          )}
          <div className="flex gap-2 mt-2">
            <Button variant="outline" className="flex-1" onClick={() => setSwitchTarget(null)} disabled={punching}>
              Annuler
            </Button>
            <Button
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={confirmSwitch}
              disabled={punching}
              data-testid="switch-confirm-btn"
            >
              {punching ? 'En cours...' : 'Confirmer la bascule'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal durée de pause */}
      <Dialog open={showPauseModal} onOpenChange={setShowPauseModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Coffee className="w-5 h-5 text-amber-600" /> Durée de la pause</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">Combien de temps avez-vous pris de pause ?</p>
          <Select value={pauseDuration} onValueChange={setPauseDuration}>
            <SelectTrigger data-testid="pause-duration-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Pas de pause</SelectItem>
              <SelectItem value="0.25">15 minutes</SelectItem>
              <SelectItem value="0.5">30 minutes</SelectItem>
              <SelectItem value="0.75">45 minutes</SelectItem>
              <SelectItem value="1">1 heure</SelectItem>
              <SelectItem value="1.5">1h30</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2 mt-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowPauseModal(false)}>Annuler</Button>
            <Button className="flex-1 bg-amber-500 hover:bg-amber-600" onClick={() => {
              const bon = pendingPunchOut?.bon;
              const pauseMin = Math.round(parseFloat(pauseDuration) * 60);
              confirmPunchOut(bon, pauseMin);
            }}>
              Confirmer la pause
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 🆕 2026-05-23 — Modal déclaration pause PM (15 min après-midi)
          Pour employés terrain à partir du 25 mai 2026. 2 étapes : question + confirmation. */}
      <Dialog open={!!pausePmModal} onOpenChange={(o) => { if (!o) setPausePmModal(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="pause-pm-modal">
          {pausePmModal?.step === 'question' && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg">Pause de l'après-midi</DialogTitle>
              </DialogHeader>
              <p className="text-base text-slate-700 mt-2">
                As-tu pris ta pause de 15 minutes cet après-midi ?
              </p>
              <div className="flex flex-col gap-2 mt-4">
                <Button
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12"
                  onClick={() => setPausePmModal({ ...pausePmModal, step: 'confirm-oui' })}
                  data-testid="pause-pm-oui-btn"
                >
                  Oui, j'ai pris ma pause de 15 minutes
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-amber-300 text-amber-800 hover:bg-amber-50 h-12"
                  onClick={() => setPausePmModal({ ...pausePmModal, step: 'confirm-non' })}
                  data-testid="pause-pm-non-btn"
                >
                  Non, je n'ai pas pris ma pause de 15 minutes
                </Button>
              </div>
              <div className="text-[11px] text-slate-500 mt-3 text-center">
                Tu peux annuler en fermant cette fenêtre. Aucun punch ne sera enregistré.
              </div>
            </>
          )}

          {pausePmModal?.step === 'confirm-oui' && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg text-emerald-700">Confirmation</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 mt-2">
                <p className="text-sm text-slate-700">
                  Tu confirmes avoir pris ta pause de 15 minutes cet après-midi.
                </p>
                <p className="text-sm text-slate-600">
                  En confirmant, tu déclares que l'information fournie est exacte. Les heures de travail seront enregistrées selon ton heure réelle de départ.
                </p>
              </div>
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setPausePmModal({ ...pausePmModal, step: 'question' })}
                  data-testid="pause-pm-retour-btn"
                >
                  Retour
                </Button>
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={punching}
                  onClick={() => {
                    const { bon, pauseMin } = pausePmModal;
                    setPausePmModal(null);
                    confirmPunchOut(bon, pauseMin, true);
                  }}
                  data-testid="pause-pm-confirm-oui-btn"
                >
                  Confirmer et enregistrer mon punch
                </Button>
              </div>
            </>
          )}

          {pausePmModal?.step === 'confirm-non' && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg text-amber-700">Confirmation</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 mt-2">
                <p className="text-sm text-slate-700">
                  Tu confirmes ne pas avoir pris ta pause de 15 minutes cet après-midi.
                </p>
                <p className="text-sm text-slate-600">
                  En confirmant, 15 minutes seront ajoutées à ton temps travaillé pour la journée. Tu déclares que l'information fournie est exacte.
                </p>
              </div>
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setPausePmModal({ ...pausePmModal, step: 'question' })}
                  data-testid="pause-pm-retour-btn-2"
                >
                  Retour
                </Button>
                <Button
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                  disabled={punching}
                  onClick={() => {
                    const { bon, pauseMin } = pausePmModal;
                    setPausePmModal(null);
                    confirmPunchOut(bon, pauseMin, false);
                  }}
                  data-testid="pause-pm-confirm-non-btn"
                >
                  Confirmer et enregistrer mon punch
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* 🆕 [2026-06-11] Dialog confirmation "hors zone" au punch-IN.
          Sobre, sans mention du seuil ni d'indulgence. */}
      <Dialog
        open={!!horsZoneConfirmIn}
        onOpenChange={(o) => { if (!o) setHorsZoneConfirmIn(null); }}
      >
        <DialogContent
          className="sm:max-w-md"
          data-testid="hors-zone-confirm-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-base">Hors zone</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-700">
              Vous êtes à{' '}
              <strong>{Math.round(horsZoneConfirmIn?.distance_m || 0)} m</strong>{' '}
              du chantier. Voulez-vous vous puncher quand même&nbsp;?
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setHorsZoneConfirmIn(null)}
                data-testid="hors-zone-non-btn"
              >
                Non
              </Button>
              <Button
                onClick={async () => {
                  const ctx = horsZoneConfirmIn;
                  setHorsZoneConfirmIn(null);
                  if (!ctx) return;
                  setPunching(true);
                  try {
                    if (ctx.isServiceCall && ctx.sc) {
                      await _executePunchInServiceCall(ctx.sc, ctx.gps);
                    } else if (ctx.bon) {
                      await _executePunchInChantier(ctx.bon, ctx.gps);
                    }
                  } catch (_e) {
                    setPunching(false);
                  }
                }}
                data-testid="hors-zone-oui-btn"
              >
                Oui
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
